/**
 * Cost Breakdown Component.
 *
 * Displays cost entries and work order costs for an asset, with totals
 * for landed cost and COGS. Work order costs show labor, parts, and
 * materials from completed work orders linked to the asset.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { Wrench } from "lucide-react";
import {
  useAssetCosts,
  useAddCostEntry,
  useAssetWorkOrders,
} from "@/features/inventory/api";
import { COST_TYPES } from "@/types/asset";
import type { AssetCostEntryCreate } from "@/types/asset";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Card from "@/components/ui/Card";

interface CostBreakdownProps {
  assetId: string;
  landedCost: number;
  totalCogs: number;
}

function CostBreakdown({ assetId, landedCost, totalCogs }: CostBreakdownProps) {
  const { data: costs, isLoading } = useAssetCosts(assetId);
  const { data: woData } = useAssetWorkOrders(assetId);
  const addCostMutation = useAddCostEntry(assetId);

  const [showForm, setShowForm] = useState(false);
  const [costType, setCostType] = useState("shipping");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");

  const workOrders = woData?.items || [];
  const woTotalCost = workOrders.reduce(
    (sum, wo) => sum + (wo.total_cost || 0),
    0,
  );

  const handleAddCost = async (e: React.FormEvent) => {
    e.preventDefault();
    const data: AssetCostEntryCreate = {
      cost_type: costType,
      amount: parseFloat(amount),
      description: description || undefined,
    };
    await addCostMutation.mutateAsync(data);
    setAmount("");
    setDescription("");
    setShowForm(false);
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);

  const woStatusVariant = (
    status: string,
  ): "default" | "success" | "warning" | "info" => {
    if (status === "Completed") return "success";
    if (status === "In Progress") return "warning";
    if (status === "Open") return "info";
    return "default";
  };

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card padding="sm">
          <p className="text-xs font-medium text-[var(--text-secondary)]">
            Landed Cost
          </p>
          <p className="mt-1 text-lg font-bold text-[var(--text-primary)]">
            {formatCurrency(landedCost)}
          </p>
        </Card>
        <Card padding="sm">
          <p className="text-xs font-medium text-[var(--text-secondary)]">
            Work Order Costs
          </p>
          <p className="mt-1 text-lg font-bold text-amber-600">
            {formatCurrency(woTotalCost)}
          </p>
          <p className="text-[10px] text-[var(--text-tertiary)]">
            {workOrders.length} work order
            {workOrders.length !== 1 ? "s" : ""}
          </p>
        </Card>
        <Card padding="sm">
          <p className="text-xs font-medium text-[var(--text-secondary)]">
            Total COGS
          </p>
          <p className="mt-1 text-lg font-bold text-[var(--text-danger)]">
            {formatCurrency(totalCogs)}
          </p>
        </Card>
      </div>

      {/* Manual Cost Entries Table */}
      <Card padding="none">
        <div className="flex items-center justify-between border-b border-[var(--border-default)] px-4 py-3">
          <h3 className="text-sm font-medium text-[var(--text-primary)]">
            Cost Entries
          </h3>
          <Button size="sm" onClick={() => setShowForm(!showForm)}>
            {showForm ? "Cancel" : "Add Cost"}
          </Button>
        </div>

        {showForm && (
          <form
            onSubmit={handleAddCost}
            className="border-b border-[var(--border-default)] bg-[var(--surface-sunken)] p-4"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)]">
                  Type
                </label>
                <select
                  value={costType}
                  onChange={(e) => setCostType(e.target.value)}
                  className="mt-1 block w-full rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-2 text-sm"
                >
                  {COST_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)]">
                  Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="mt-1 block w-full rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)]">
                  Description
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional"
                  className="mt-1 block w-full rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <Button
                type="submit"
                size="sm"
                isLoading={addCostMutation.isPending}
              >
                Add Entry
              </Button>
            </div>
          </form>
        )}

        {isLoading ? (
          <p className="px-4 py-6 text-center text-sm text-[var(--text-tertiary)]">
            Loading...
          </p>
        ) : costs && costs.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border-default)] bg-[var(--surface-sunken)] text-left text-xs font-medium text-[var(--text-secondary)]">
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">Description</th>
                <th className="px-4 py-2 text-right">Amount</th>
                <th className="px-4 py-2">Date</th>
              </tr>
            </thead>
            <tbody>
              {costs.map((entry) => (
                <tr
                  key={entry.id}
                  className="border-b border-[var(--border-subtle)]"
                >
                  <td className="px-4 py-2 capitalize">{entry.cost_type}</td>
                  <td className="px-4 py-2 text-[var(--text-secondary)]">
                    {entry.description || "\u2014"}
                  </td>
                  <td className="px-4 py-2 text-right font-medium">
                    {formatCurrency(entry.amount)}
                  </td>
                  <td className="px-4 py-2 text-[var(--text-secondary)]">
                    {new Date(entry.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="px-4 py-6 text-center text-sm text-[var(--text-tertiary)]">
            No cost entries yet.
          </p>
        )}
      </Card>

      {/* Work Order Costs */}
      {workOrders.length > 0 && (
        <Card padding="none">
          <div className="border-b border-[var(--border-default)] px-4 py-3">
            <h3 className="text-sm font-medium text-[var(--text-primary)]">
              Work Order Costs
            </h3>
            <p className="text-[10px] text-[var(--text-tertiary)] mt-0.5">
              Labor, parts, and materials from linked work orders.
            </p>
          </div>
          <div className="divide-y divide-[var(--border-subtle)]">
            {workOrders.map((wo) => (
              <Link
                key={wo.id}
                to={`/shop/${wo.id}`}
                className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--surface-sunken)] transition-colors"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                  <Wrench className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono font-semibold text-[var(--text-primary)]">
                      {wo.wo_number}
                    </span>
                    <Badge variant={woStatusVariant(wo.status)}>
                      {wo.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-[var(--text-secondary)] truncate mt-0.5">
                    {wo.title}
                  </p>
                  <div className="flex gap-3 mt-0.5 text-[10px] text-[var(--text-tertiary)]">
                    {wo.total_labor_cost > 0 && (
                      <span>Labor: {formatCurrency(wo.total_labor_cost)}</span>
                    )}
                    {wo.total_parts_cost > 0 && (
                      <span>Parts: {formatCurrency(wo.total_parts_cost)}</span>
                    )}
                    {wo.total_materials_cost > 0 && (
                      <span>
                        Materials: {formatCurrency(wo.total_materials_cost)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-bold text-[var(--text-primary)]">
                    {formatCurrency(wo.total_cost)}
                  </p>
                  <p className="text-[10px] text-[var(--text-tertiary)]">
                    {new Date(wo.created_at).toLocaleDateString()}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

export default CostBreakdown;
